/**
 * Truncate `.property__clamp` when content overflows the CSS max-height.
 * Expand-only: the toggle never collapses again.
 *
 * Markup:
 *   .property__clamp[data-clamp="mobile"]  — clamp only below ScreenSM
 *   .property__clamp[data-analytics-section="overview"|"description"]
 *   .property__clamp-body
 *   .property__clamp-toggle[hidden]
 */
import { pushAnalyticsEvent } from "./analytics.js";

/**
 * @param {Element} clamp
 * @returns {"overview"|"description"|null}
 */
function getAnalyticsSection(clamp) {
  const section = clamp.getAttribute("data-analytics-section");
  if (section === "overview" || section === "description") {
    return section;
  }

  return null;
}

function initPropertyClamp(root = document) {
  const DURATION_MS = 320;
  const EASING = "ease";
  const mobileMq = window.matchMedia(`(max-width: ${window.ScreenSM}px)`);
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

  function isMobileOnly(clamp) {
    return clamp.getAttribute("data-clamp") === "mobile";
  }

  function update(clamp) {
    if (clamp.classList.contains("is-expanded")) {
      return;
    }

    const body = clamp.querySelector(".property__clamp-body");
    const toggle = clamp.querySelector(".property__clamp-toggle");
    if (!body || !toggle) {
      return;
    }

    if (isMobileOnly(clamp) && !mobileMq.matches) {
      clamp.classList.remove("is-clamped");
      toggle.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
      return;
    }

    clamp.classList.add("is-clamped");
    const overflows = body.scrollHeight > body.clientHeight + 1;

    if (!overflows) {
      clamp.classList.remove("is-clamped");
      toggle.hidden = true;
      toggle.setAttribute("aria-expanded", "false");
      return;
    }

    toggle.hidden = false;
    toggle.setAttribute("aria-expanded", "false");
  }

  function finishExpand(clamp, body) {
    clamp.classList.remove("is-expanding");
    body.style.height = "";
    body.style.overflow = "";
  }

  function expand(clamp) {
    const body = clamp.querySelector(".property__clamp-body");
    const toggle = clamp.querySelector(".property__clamp-toggle");
    if (!body || clamp.classList.contains("is-expanded")) {
      return;
    }

    clamp.classList.add("is-expanded");
    if (toggle) {
      toggle.hidden = true;
      toggle.setAttribute("aria-expanded", "true");
    }

    const section = getAnalyticsSection(clamp);
    if (section) {
      pushAnalyticsEvent("property_content_expand", { section });
    }

    const startHeight = body.clientHeight;
    clamp.classList.add("is-expanding");
    clamp.classList.remove("is-clamped");

    if (reducedMotion.matches || !body.animate) {
      finishExpand(clamp, body);
      return;
    }

    body.style.overflow = "hidden";
    body.style.height = `${startHeight}px`;
    const targetHeight = body.scrollHeight;

    const animation = body.animate(
      [{ height: `${startHeight}px` }, { height: `${targetHeight}px` }],
      { duration: DURATION_MS, easing: EASING },
    );

    animation.onfinish = () => finishExpand(clamp, body);
    animation.oncancel = () => finishExpand(clamp, body);
  }

  const clamps = root.querySelectorAll(".property__clamp");
  if (!clamps.length) {
    return;
  }

  clamps.forEach((clamp) => {
    update(clamp);

    const toggle = clamp.querySelector(".property__clamp-toggle");
    if (!toggle) {
      return;
    }

    toggle.addEventListener("click", () => {
      expand(clamp);
    });
  });

  const refresh = () => {
    clamps.forEach(update);
  };

  window.addEventListener("resize", refresh);
  if (typeof mobileMq.addEventListener === "function") {
    mobileMq.addEventListener("change", refresh);
  } else if (typeof mobileMq.addListener === "function") {
    mobileMq.addListener(refresh);
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => initPropertyClamp());
} else {
  initPropertyClamp();
}
